import { useEffect, useState } from "react";
import { fetchCalendarSuggestions, confirmCalendarSuggestion, dismissCalendarSuggestion } from "@/lib/api";
import { CALENDAR_PAGE } from "@/constants/testIds";
import { formatDay, formatTime, childChip } from "@/lib/format";
import { Calendar as CalIcon, Check, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import useTrackPage from "@/hooks/useTrackPage";

export default function CalendarPage() {
  useTrackPage("calendar_open");
  const [items, setItems] = useState([]);
  const load = () => fetchCalendarSuggestions().then(setItems);
  useEffect(() => { load(); }, []);

  const groups = items.reduce((acc, s) => {
    const day = s.event_date ? new Date(s.event_date).toDateString() : "Undated";
    (acc[day] ||= []).push(s); return acc;
  }, {});

  const confirm = async (s) => { await confirmCalendarSuggestion(s.id); toast.success("Added to calendar"); load(); };
  const dismiss = async (s) => { await dismissCalendarSuggestion(s.id); toast("Dismissed"); load(); };

  return (
    <div data-testid={CALENDAR_PAGE.page} className="space-y-8">
      <div>
        <p className="label-overline">Calendar</p>
        <h1 className="font-display text-4xl text-[var(--fcc-ink)] mt-2">Suggestions and confirmed events</h1>
        <p className="text-[var(--fcc-ink-soft)] mt-2 text-sm max-w-2xl">Confirm what belongs on the family calendar. Confirmed items are highlighted — dismissed ones disappear from this view.</p>
      </div>

      {Object.entries(groups).map(([day, list]) => (
        <section key={day}>
          <h2 className="font-display text-2xl text-[var(--fcc-ink)] mb-4">{day !== "Undated" ? new Date(day).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) : "Undated"}</h2>
          <ul className="grid md:grid-cols-2 gap-4">
            {list.map(s => (
              <li key={s.id} className={`fcc-card p-6 ${s.confirmed ? "ring-2 ring-[var(--fcc-green)]" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-xl text-[var(--fcc-ink)]">{s.title}</p>
                    <p className="text-sm text-[var(--fcc-ink-soft)] mt-1">{formatTime(s.event_date)}</p>
                  </div>
                  <span className={`fcc-pill ${childChip(s.child_name)}`}>{s.child_name || "Family"}</span>
                </div>
                {s.location && <p className="text-xs text-[var(--fcc-muted)] mt-3 flex items-center gap-1"><MapPin className="w-3 h-3" />{s.location}</p>}
                <p className="text-sm text-[var(--fcc-ink-soft)] mt-3 leading-relaxed">{s.notes}</p>
                <div className="mt-5 flex gap-2">
                  {!s.confirmed && <button data-testid={CALENDAR_PAGE.confirm} onClick={() => confirm(s)} className="fcc-btn-primary text-sm py-2 px-4"><Check className="w-4 h-4" /> Confirm</button>}
                  {s.confirmed && <span className="fcc-pill bg-[var(--fcc-green-soft)] text-[var(--fcc-green)]"><Check className="w-3 h-3" /> Confirmed</span>}
                  <button data-testid={CALENDAR_PAGE.dismiss} onClick={() => dismiss(s)} className="fcc-btn-outline text-sm py-2 px-4"><X className="w-4 h-4" /> Dismiss</button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {items.length === 0 && (
        <div className="fcc-card p-10 text-center text-[var(--fcc-muted)]"><CalIcon className="w-6 h-6 mx-auto" /><p className="mt-2">No suggestions right now.</p></div>
      )}
    </div>
  );
}
